import React, { useEffect, useState } from "react";
import { Globe, ChevronDown, ChevronRight, Loader2, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import SavePublishBar from "./SavePublishBar";

/**
 * URL Translator panel.
 * Paste a foreign-language URL → AI fetches the page, translates it into a chosen language,
 * and returns a SUMMARY + FULL TRANSLATION. Embedded on the Deep Dive page so researchers
 * can quickly read Russian/Chinese/Arabic press without leaving the workflow.
 */
export default function UrlTranslator() {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState("");
  const [target, setTarget] = useState("en");
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get("/ai/languages").then((r) => setLanguages(r.data?.items || [])).catch(() => {});
  }, []);

  const onSubmit = async (e) => {
    e?.preventDefault();
    if (!url.trim()) return;
    setLoading(true); setResult(null);
    try {
      const r = await api.post("/ai/translate-url", { url: url.trim(), target_lang: target });
      setResult(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Translation failed");
    } finally { setLoading(false); }
  };

  const onCopy = async () => {
    if (!result?.result) return;
    try {
      await navigator.clipboard.writeText(result.result);
      setCopied(true); setTimeout(() => setCopied(false), 1500);
    } catch { toast.error("Copy failed"); }
  };

  return (
    <div className="border border-[#a78bfa]/30 bg-[#0a0a0a]" data-testid="url-translator">
      <button type="button" onClick={() => setOpen(!open)} data-testid="url-translator-toggle"
        className="w-full text-left px-4 py-3 flex items-center gap-2 hover:bg-[#a78bfa]/[0.08] transition-colors">
        {open ? <ChevronDown size={14} className="text-[#a78bfa]" /> : <ChevronRight size={14} className="text-[#a78bfa]" />}
        <Globe size={14} className="text-[#a78bfa]" />
        <span className="font-mono text-[11px] tracking-[0.2em] text-[#a78bfa]">URL TRANSLATOR — FOREIGN SOURCE → READABLE</span>
      </button>
      {open && (
        <div className="px-4 pb-4 pt-1 border-t border-[#a78bfa]/20">
          <p className="text-[11px] text-zinc-400 leading-relaxed mb-3">
            Paste a foreign-language article URL (Russian press, German FOIA, Chinese news, Wikipedia in any
            language). AI fetches the page and returns a 3–6 bullet summary plus a full translation in your
            chosen language.
          </p>
          <form onSubmit={onSubmit} className="flex flex-col gap-2 mb-3">
            <input type="url" required value={url} onChange={(e) => setUrl(e.target.value)}
              data-testid="url-translator-input" placeholder="https://lenta.ru/articles/…"
              className="w-full bg-[#0a0a0a] border border-white/15 focus:border-[#a78bfa] outline-none px-3 py-2 font-mono text-sm text-white transition-colors" />
            <div className="flex gap-2 flex-wrap">
              <select value={target} onChange={(e) => setTarget(e.target.value)} data-testid="url-translator-lang"
                className="flex-1 min-w-[140px] bg-[#0a0a0a] border border-white/15 focus:border-[#a78bfa] outline-none px-3 py-2 font-mono text-sm text-white transition-colors">
                {languages.map((l) => (
                  <option key={l.code} value={l.code}>{l.name} — {l.native}</option>
                ))}
              </select>
              <button type="submit" disabled={loading || !url.trim()} data-testid="url-translator-submit"
                className="px-5 py-2 font-mono text-[11px] uppercase tracking-[0.2em] bg-[#a78bfa] text-[#050505] hover:bg-white transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2 whitespace-nowrap">
                {loading ? <Loader2 size={12} className="animate-spin" /> : <Globe size={12} />}
                {loading ? "Translating…" : "Translate"}
              </button>
            </div>
          </form>
          {result && (
            <div data-testid="url-translator-result" className="border border-white/10 bg-[#050505] p-4">
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="font-mono text-[9px] tracking-[0.25em] text-zinc-500">
                  → {result.language_name} · <a href={result.url} target="_blank" rel="noreferrer" className="underline hover:text-[#a78bfa]">SOURCE</a>
                </div>
                <button type="button" onClick={onCopy} data-testid="url-translator-copy"
                  className="inline-flex items-center gap-1 px-2 py-1 font-mono text-[9px] tracking-[0.2em] border border-white/15 text-zinc-400 hover:text-white hover:border-white/30 transition-colors">
                  {copied ? <Check size={10} /> : <Copy size={10} />} {copied ? "COPIED" : "COPY"}
                </button>
              </div>
              <pre className="whitespace-pre-wrap text-sm text-zinc-200 leading-relaxed font-mono">{result.result}</pre>
              <SavePublishBar
                kind="url_translator"
                topic={result.url || url}
                getPayload={() => result}
                testId="url-translator-save"
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
